// /inquiries — 询盘列表
// v1.9 P0: 移动端表格退化为 card list (用 ResponsiveTable 包装)
// v1.13: 加 source filter chip + 'web3forms' source label
// v1.22: 行可点跳详情页
// v1.26.2 fix: 用 TanStack Router navigate() 替代 window.location.href
//   (整页 reload 会丢失 zustand store + 重新 mount, 详情页 useQuery 触发新 fetch 时
//   仍有 race; SPA 内导航保留 store + route state, 详情页 query 命中 cache)
// v1.27.2 fix: 父 route 加 <Outlet /> 渲染 /inquiries/$inquiryId 子路由
//   (routeTree.gen.ts 把 $inquiryId 注册为 list 的子路由, 之前没有 Outlet → 切到详情时
//    URL 改了但内容还是 list, 表现为"点了没反应")
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { CardListSkeleton, TableSkeleton } from '../components/Skeleton';
import { type Inquiry, inquiryApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/inquiries')({
  component: InquiriesPage,
});

const STATUS_LABELS: Record<Inquiry['status'], string> = {
  new: '新询盘',
  quoted: '已报价',
  sampled: '已样品',
  ordered: '已下单',
  rejected: '流失',
  archived: '已归档',
};

const STATUS_COLORS: Record<Inquiry['status'], string> = {
  new: 'tag tag-stage-quoted',
  quoted: 'tag tag-good',
  sampled: 'tag tag-stage-sampled',
  ordered: 'tag tag-good',
  rejected: 'tag',
  archived: 'tag',
};

const LANG_FLAGS: Record<string, string> = {
  zh: '🇨🇳',
  en: '🇺🇸',
  ja: '🇯🇵',
};

const SOURCE_LABELS: Record<string, string> = {
  email: '邮件',
  linkedin: 'LinkedIn',
  jimoto: 'jimoto',
  exhibition: '展会',
  referral: '介绍',
  alibaba: 'Alibaba',
  thomasnet: 'ThomasNet',
  kompass: 'Kompass',
  website: '官网',
  web3forms: '官网表单',
  manual: '手动',
};

// v1.13: source filter chips
const SOURCE_CHIPS: { key: string; label: string; color: string }[] = [
  { key: 'all', label: '全部', color: 'bg-surface-2 text-ink-2 border-line' },
  { key: 'web3forms', label: '🌐 官网表单', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'email', label: '✉️ 邮件', color: 'bg-surface-2 text-ink-2 border-line' },
  { key: 'linkedin', label: 'in LinkedIn', color: 'bg-surface-2 text-ink-2 border-line' },
  { key: 'exhibition', label: '🏛 展会', color: 'bg-surface-2 text-ink-2 border-line' },
  { key: 'manual', label: '✍️ 手动', color: 'bg-surface-2 text-ink-2 border-line' },
];

function InquiriesPage() {
  const navigate = useNavigate();
  const { currentCompanyId } = useAuthStore();
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  // v1.36+: 视图模式 (default 默认列表 / ai-summary 显示 AI 提取的产品摘要列)
  const [viewMode, setViewMode] = useState<'default' | 'ai-summary'>('default');
  // v1.27.2 fix: 父 route 必须包含 <Outlet /> 才能渲染 /inquiries/$inquiryId 子路由
  // 详情页 path 匹配时只渲染 Outlet (不渲染 list), 否则渲染 list
  // 注意: hooks 必须在 early return 之前全部调用, 否则违反 React hook 顺序规则
  const location = useLocation();
  const onDetail =
    location.pathname.startsWith('/inquiries/') && location.pathname !== '/inquiries';
  const { data, isLoading } = useQuery({
    queryKey: ['inquiries', currentCompanyId, sourceFilter],
    queryFn: () => inquiryApi.list(),
    enabled: !onDetail, // v1.27.2: 在 detail 页面时不触发 list query
  });

  // 客户端二次过滤 (后端 list 不支持 source 过滤, 走 zod schema 重构前用前端过滤)
  const filteredItems = (data?.items ?? []).filter(
    (i) => sourceFilter === 'all' || i.source === sourceFilter,
  );

  if (onDetail) return <Outlet />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold mb-1">询盘</h1>
          <p className="text-ink-3 text-xs sm:text-sm">共 {data?.total ?? 0} 条 · 当前公司视角</p>
        </div>
        <div className="flex items-center gap-2">
          {/* v1.36+: 视图模式切换按钮 */}
          <div className="inline-flex rounded border border-line bg-surface">
            <button
              type="button"
              onClick={() => setViewMode('default')}
              className={`px-3 py-1.5 text-xs rounded-l transition-colors ${
                viewMode === 'default'
                  ? 'bg-accent text-white font-medium'
                  : 'text-ink-2 hover:bg-surface-2'
              }`}
              title="默认视图 (主题/来源/语种/状态/时间)"
            >
              📋 默认
            </button>
            <button
              type="button"
              onClick={() => setViewMode('ai-summary')}
              className={`px-3 py-1.5 text-xs rounded-r transition-colors border-l border-line ${
                viewMode === 'ai-summary'
                  ? 'bg-accent text-white font-medium'
                  : 'text-ink-2 hover:bg-surface-2'
              }`}
              title="AI 摘要视图 (类目/数量/工艺) — 一眼扫到客户要什么"
            >
              🤖 AI 摘要
            </button>
          </div>
        </div>
      </div>

      {/* v1.13: source filter chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {SOURCE_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setSourceFilter(c.key)}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
              sourceFilter === c.key
                ? `${c.color} ring-1 ring-accent/40 font-medium`
                : `${c.color} hover:bg-surface`
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="bg-surface border border-line rounded overflow-hidden">
        {isLoading ? (
          <>
            <div className="hidden md:block">
              <TableSkeleton rows={5} />
            </div>
            <div className="md:hidden p-2">
              <CardListSkeleton rows={5} />
            </div>
          </>
        ) : filteredItems.length === 0 ? (
          <EmptyState
            icon="mail"
            title={sourceFilter === 'all' ? '还没有询盘' : '此来源下没有询盘'}
            description={
              sourceFilter === 'all'
                ? '从「找客」搜出潜在客户后手动录入, 或在客户详情里点击「记询盘」'
                : '切换其他来源或等待官网表单新提交'
            }
          />
        ) : (
          <>
            {/* 桌面: 表格 */}
            <table className="hidden md:table w-full">
              <thead>
                <tr className="bg-surface-2 text-ink-2 text-xs">
                  <th className="text-left px-4 py-2 font-medium">主题</th>
                  {viewMode === 'default' ? (
                    <>
                      <th className="text-left px-4 py-2 font-medium">来源</th>
                      <th className="text-left px-4 py-2 font-medium">语种</th>
                      <th className="text-left px-4 py-2 font-medium">状态</th>
                      <th className="text-left px-4 py-2 font-medium">收到时间</th>
                    </>
                  ) : (
                    <>
                      {/* AI 摘要视图: 替换 来源/语种/状态/时间 为 AI 提取的关键字段 */}
                      <th className="text-left px-4 py-2 font-medium">类目</th>
                      <th className="text-left px-4 py-2 font-medium">数量</th>
                      <th className="text-left px-4 py-2 font-medium">工艺</th>
                      <th className="text-left px-4 py-2 font-medium">紧急/预算</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((i) => {
                  const hint = (i as { productHint?: { category?: string; qty?: number; craft?: string[]; urgency?: string; budget?: number | string } }).productHint;
                  return (
                  <tr
                    key={i.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`打开询盘 ${i.subject || i.id}`}
                    className="border-t border-line hover:bg-surface-2 cursor-pointer focus:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/30"
                    onClick={() =>
                      navigate({ to: '/inquiries/$inquiryId', params: { inquiryId: i.id } })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate({ to: '/inquiries/$inquiryId', params: { inquiryId: i.id } });
                      }
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{i.subject || '(无主题)'}</div>
                      {i.sourceRef && (
                        <div className="text-xs text-ink-3 mt-0.5 truncate max-w-md">
                          {i.sourceRef}
                        </div>
                      )}
                      {/* AI 摘要视图: 主题下加 来源/语种 角标 (信息密度高) */}
                      {viewMode === 'ai-summary' && (
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-ink-3">
                          <span className="inline-flex items-center gap-0.5">
                            {LANG_FLAGS[i.language]} {i.language.toUpperCase()}
                          </span>
                          <span>·</span>
                          <span>{SOURCE_LABELS[i.source] ?? i.source}</span>
                          <span>·</span>
                          <span>{new Date(i.receivedAt).toLocaleDateString('zh-CN')}</span>
                        </div>
                      )}
                    </td>
                    {viewMode === 'default' ? (
                      <>
                        <td className="px-4 py-3 text-xs text-ink-2">
                          {i.source === 'web3forms' ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-200"
                              title="来自 toei-sosan.com 官网联系表单"
                            >
                              🌐 {SOURCE_LABELS[i.source] ?? i.source}
                            </span>
                          ) : (
                            (SOURCE_LABELS[i.source] ?? i.source)
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {LANG_FLAGS[i.language]} {i.language.toUpperCase()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={STATUS_COLORS[i.status]}>{STATUS_LABELS[i.status]}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-ink-3">
                          {new Date(i.receivedAt).toLocaleString('zh-CN')}
                        </td>
                      </>
                    ) : (
                      <>
                        {/* AI 摘要视图列 */}
                        <td className="px-4 py-3 text-sm">
                          {hint?.category ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">
                              {hint.category}
                            </span>
                          ) : (
                            <span className="text-ink-3 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {hint?.qty != null ? (
                            <span className="font-mono text-sm">{hint.qty}</span>
                          ) : (
                            <span className="text-ink-3 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-ink-2">
                          {hint?.craft && Array.isArray(hint.craft) && hint.craft.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {hint.craft.slice(0, 3).map((c) => (
                                <span
                                  key={c}
                                  className="inline-block px-1.5 py-0.5 bg-surface-2 rounded text-[10px] text-ink-3"
                                >
                                  {c}
                                </span>
                              ))}
                              {hint.craft.length > 3 && (
                                <span className="text-ink-3 text-[10px]">+{hint.craft.length - 3}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-ink-3 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {hint?.urgency ? (
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] mb-0.5 ${
                              String(hint.urgency).toLowerCase().includes('urgent') ||
                              String(hint.urgency).includes('急') ||
                              String(hint.urgency).toLowerCase().includes('asap')
                                ? 'bg-bad/10 text-bad font-medium'
                                : 'bg-surface-2 text-ink-2'
                            }`}>
                              {hint.urgency}
                            </span>
                          ) : null}
                          {hint?.budget != null && hint.budget !== '' && (
                            <div className="text-ink-2">
                              💰 {String(hint.budget)}
                            </div>
                          )}
                          {!hint?.urgency && (hint?.budget == null || hint.budget === '') && (
                            <span className="text-ink-3 text-xs">—</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* 移动: 卡片列表 (v1.27.2 fix: 补 onClick + role/tabIndex, 之前没绑 click handler 移动端点不动) */}
            <ul className="md:hidden p-2 space-y-2">
              {filteredItems.map((i) => {
                const hint = (i as { productHint?: { category?: string; qty?: number; craft?: string[]; urgency?: string; budget?: number | string } }).productHint;
                return (
                <li
                  key={i.id}
                  // biome-ignore lint/a11y/useSemanticElements: <li> 不能套 <button>, 整卡片可点跳详情, 加 role/aria-label 提升 a11y
                  // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: <li> 升级为可点击行, role/tabIndex/onKeyDown 配套
                  role="button"
                  tabIndex={0}
                  aria-label={`打开询盘 ${i.subject || i.id}`}
                  className="bg-surface-2 border border-line rounded-lg p-3 animate-fade-up active:bg-surface focus:outline-none focus:ring-2 focus:ring-accent/30"
                  onClick={() =>
                    navigate({ to: '/inquiries/$inquiryId', params: { inquiryId: i.id } })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate({ to: '/inquiries/$inquiryId', params: { inquiryId: i.id } });
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-ink truncate">
                        {i.subject || '(无主题)'}
                      </div>
                      {i.sourceRef && (
                        <div className="text-xs text-ink-3 mt-0.5 truncate">{i.sourceRef}</div>
                      )}
                    </div>
                    <span className={`${STATUS_COLORS[i.status]} shrink-0`}>
                      {STATUS_LABELS[i.status]}
                    </span>
                  </div>
                  {/* v1.36+: AI 摘要视图 (移动端显示在 status 下方) */}
                  {viewMode === 'ai-summary' && hint && (
                    <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
                      {hint.category && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent/10 text-accent font-medium">
                          {hint.category}
                        </span>
                      )}
                      {hint.qty != null && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-surface text-ink-2">
                          ×{hint.qty}
                        </span>
                      )}
                      {hint.urgency && (
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ${
                          String(hint.urgency).toLowerCase().includes('urgent') ||
                          String(hint.urgency).includes('急')
                            ? 'bg-bad/10 text-bad font-medium'
                            : 'bg-surface text-ink-2'
                        }`}>
                          ⚡{hint.urgency}
                        </span>
                      )}
                      {hint.budget != null && hint.budget !== '' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-surface text-ink-2">
                          💰{String(hint.budget)}
                        </span>
                      )}
                    </div>
                  )}
                  <dl className="text-xs space-y-0.5 mt-1.5">
                    <div className="flex gap-2">
                      <dt className="text-ink-3 w-14 shrink-0">来源</dt>
                      <dd className="text-ink-2">{SOURCE_LABELS[i.source] ?? i.source}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-ink-3 w-14 shrink-0">语种</dt>
                      <dd className="text-ink-2">
                        {LANG_FLAGS[i.language]} {i.language.toUpperCase()}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-ink-3 w-14 shrink-0">收到</dt>
                      <dd className="text-ink-3">
                        {new Date(i.receivedAt).toLocaleString('zh-CN')}
                      </dd>
                    </div>
                  </dl>
                </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
